import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, Heart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  'Todos',
  'Llaveros',
  'Lectura',
  'Imanes',
  'Mini cosas',
  'Accesorios',
  'Decoración'
];

function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const { addToCart, toggleFavorite, isFavorite } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchTerm]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'Todos') params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const { data } = await axios.get(`${API_URL}/api/products`, { params });
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    const result = await addToCart(product.product_id, 1, {}, product);
    if (result.success) {
      toast.success('Producto añadido al carrito');
    } else {
      toast.error('Error al añadir al carrito');
    }
  };

  const handleToggleFavorite = async (productId) => {
    await toggleFavorite(productId);
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-[#374963] mb-4" data-testid="catalog-page-title">
            Catálogo de Productos
          </h1>
          <p className="text-lg text-[#73706e]">
            Explora nuestra colección de productos personalizados
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#73706e] w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]"
              data-testid="catalog-search-input"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-12">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  selectedCategory === category
                    ? 'bg-[#374963] text-white'
                    : 'bg-white text-[#73706e] hover:bg-[#374963] hover:text-white'
                } shadow-[0_4px_16px_rgba(0,0,0,0.04)]`}
                data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-[#73706e]">Cargando productos...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#73706e]">No se encontraron productos</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product) => (
              <div
                key={product.product_id}
                className="bg-white rounded-xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.08)] transition-all group"
                data-testid={`product-card-${product.product_id}`}
              >
                <Link to={`/producto/${product.product_id}`} className="block relative overflow-hidden">
                  <img
                    src={product.images && product.images[0] ? product.images[0] : 'https://via.placeholder.com/400x300?text=Producto'}
                    alt={product.name}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleFavorite(product.product_id);
                    }}
                    className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:scale-110 transition-transform"
                    data-testid={`favorite-button-${product.product_id}`}
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        isFavorite(product.product_id) ? 'fill-[#785534] text-[#785534]' : 'text-[#73706e]'
                      }`}
                    />
                  </button>
                </Link>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#374963] mb-2">{product.name}</h3>
                  <p className="text-[#73706e] text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-semibold text-[#374963]">{product.price.toFixed(2)}€</span>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="px-4 py-2 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors"
                      data-testid={`add-to-cart-${product.product_id}`}
                    >
                      Añadir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CatalogPage;